import { DefinitionLevels } from '@linearb/linjs-common';
import useSettingsService from './useSettingsService';
import { getFormattedDailyTime } from 'views/root/views/home/views/settings/views/General/general-settings.service';
import { formatUTCToTimezone } from 'common/utils/utils';
import { SETTING_NAMES, TIME_PICKER_FORMAT } from 'common/constants/constants';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';

/**
 * Custom hook to retrieve and manage customization options based on the selected team or organization.
 * @param {object} params - Parameters for the hook.
 * @param {string | null} [params.selectedTeamId] - The ID of the selected team. If not provided, settings are retrieved at the organization level.
 * @returns {object} - An object containing the following customization options:
 * workingDays - Array of working days.
 * dailyTime - Formatted daily time.
 * displayDailyTime - Display formatted daily time in the specified timezone format.
 * metricsBenchmarksEnabled - Whether metrics benchmarks are enabled.
 * showPersonalInformation - Whether personal information is shown.
 * metricsCalculationStrategy - The strategy used for metrics calculation.
 * hideNonWorkingDays - Whether non-working days are hidden.
 */
const useCustomizationOptions = ({ selectedTeamId }) => {
  const { getSettingOrDefinitionValue, getTeamOrOrgSetting } =
    useSettingsService();

  const workingDays = getTeamOrOrgSetting({
    name: 'workingDays',
    ...(selectedTeamId
      ? { definitionLevel: DefinitionLevels.ORGANIZATION }
      : { definitionLevel: DefinitionLevels.TEAM, teamId: selectedTeamId })
  });

  const dailyTime = getFormattedDailyTime({
    getTeamOrOrgSetting,
    isOrgSettings: selectedTeamId ? false : true,
    selectedTeamId
  });

  const displayDailyTime = formatUTCToTimezone(dailyTime, TIME_PICKER_FORMAT);

  const metricsBenchmarksEnabled = getSettingOrDefinitionValue({
    name: SETTING_NAMES.METRICS_BENCHMARKS_ENABLED,
    definitionLevel: selectedTeamId
      ? DefinitionLevels.TEAM
      : DefinitionLevels.ORGANIZATION,
    ...(selectedTeamId ? { teamId: selectedTeamId } : {})
  });

  const showPersonalInformation = getSettingOrDefinitionValue({
    name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION
  });

  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;

  const hideNonWorkingDays = getSettingOrDefinitionValue({
    name: SETTING_NAMES.HIDE_NON_WORKING_DAY,
    definitionLevel: DefinitionLevels.ORGANIZATION
  });

  return {
    workingDays,
    dailyTime,
    displayDailyTime,
    metricsBenchmarksEnabled,
    showPersonalInformation,
    metricsCalculationStrategy,
    hideNonWorkingDays
  };
};

// eslint-disable-next-line import/no-unused-modules
export default useCustomizationOptions;
